/**
 * Brands API Client
 *
 * API functions for brand management.
 */

import type {
  Brand,
  BrandsListResponse,
  BrandResponse,
  CreateBrandRequest,
  CreateBrandResponse,
  UpdateBrandRequest,
  BrandSyncResult,
  UnlockAIDescriptionResponse,
  BrandSource,
  BrandAssets,
  BrandAssetType,
  BrandAssetSlot,
} from '@/types/brands';
import { apiClient } from '../api-client';

const API_BASE = '/api/v1/brands';

/**
 * Get all brands for workspace.
 *
 * @param source - Optional filter by source
 */
export async function getBrands(source?: BrandSource): Promise<Brand[]> {
  const params = new URLSearchParams();
  if (source) {
    params.append('source', source);
  }

  const url = source ? `${API_BASE}?${params}` : API_BASE;
  const response = await apiClient.get<BrandsListResponse>(url);
  return response.data.brands;
}

/**
 * Get single brand by ID.
 */
export async function getBrand(brandId: string): Promise<Brand> {
  const response = await apiClient.get<BrandResponse>(`${API_BASE}/${brandId}`);
  return response.data.brand;
}

/**
 * Create new brand (manual).
 */
export async function createBrand(data: CreateBrandRequest): Promise<string> {
  const response = await apiClient.post<CreateBrandResponse>(API_BASE, data);
  return response.data.brand_id;
}

/**
 * Update brand.
 */
export async function updateBrand(
  brandId: string,
  data: UpdateBrandRequest
): Promise<void> {
  await apiClient.patch(`${API_BASE}/${brandId}`, data);
}

/**
 * Delete brand (only if not assigned to products).
 */
export async function deleteBrand(brandId: string): Promise<void> {
  await apiClient.delete(`${API_BASE}/${brandId}`);
}

/**
 * Trigger sync from JTL.
 */
export async function syncBrands(): Promise<BrandSyncResult> {
  const response = await apiClient.post<BrandSyncResult>(`${API_BASE}/sync`);
  return response.data;
}

/**
 * Unlock AI description for editing (1 hour).
 */
export async function unlockAIDescription(brandId: string): Promise<string> {
  const response = await apiClient.patch<UnlockAIDescriptionResponse>(
    `${API_BASE}/${brandId}/unlock-ai-description`
  );
  return response.data.unlocked_until;
}

/**
 * Generate AI description for brand using AI tool config.
 *
 * @param brandId - Brand UUID
 * @param options - Optional override parameters
 * @returns Generated AI description with metadata
 */
export async function generateAIDescription(
  brandId: string,
  options?: {
    system_prompt?: string;
    user_prompt?: string;
    model_id?: string;
  }
): Promise<{
  brand_id: string;
  ai_description: string;
  system_prompt_used: string;
  user_prompt_used: string;
  model_used: string;
  credits_used: number;
}> {
  const response = await apiClient.post<{
    brand_id: string;
    ai_description: string;
    system_prompt_used: string;
    user_prompt_used: string;
    model_used: string;
    credits_used: number;
  }>(`${API_BASE}/${brandId}/generate-description`, options || {});
  return response.data;
}

/**
 * Upload media to brand (max 5 images).
 *
 * @param brandId - Brand UUID
 * @param file - Image file (max 10MB)
 * @returns Updated brand with new media
 */
export async function uploadBrandMedia(
  brandId: string,
  file: File
): Promise<Brand> {
  const formData = new FormData();
  formData.append('file', file);

  const response = await apiClient.post<BrandResponse>(
    `${API_BASE}/${brandId}/media`,
    formData
  );
  return response.data.brand;
}

/**
 * Delete media from brand.
 *
 * @param brandId - Brand UUID
 * @param s3Key - S3 key of media to delete
 * @returns Updated brand without deleted media
 */
export async function deleteBrandMedia(
  brandId: string,
  s3Key: string
): Promise<Brand> {
  const response = await apiClient.delete<BrandResponse>(
    `${API_BASE}/${brandId}/media`,
    { data: { s3_key: s3Key } }
  );
  return response.data.brand;
}

/**
 * Reorder brand media.
 *
 * @param brandId - Brand UUID
 * @param order - Ordered list of s3_keys
 * @returns Updated brand with reordered media
 */
export async function reorderBrandMedia(
  brandId: string,
  order: string[]
): Promise<Brand> {
  const response = await apiClient.patch<BrandResponse>(
    `${API_BASE}/${brandId}/media/reorder`,
    { order }
  );
  return response.data.brand;
}

// =============================================================================
// Brand Assets API (NEW - 2026-01-19)
// =============================================================================

/**
 * Get all brand assets organized by type and slot.
 *
 * @param brandId - Brand UUID
 * @returns Brand assets with 2 logo slots + 2 style image slots
 */
export async function getBrandAssets(brandId: string): Promise<BrandAssets> {
  const response = await apiClient.get<BrandAssets>(`${API_BASE}/${brandId}/assets`);
  return response.data;
}

/**
 * Assign an asset to a brand slot.
 *
 * @param brandId - Brand UUID
 * @param assetId - Asset UUID (from generated_images table)
 * @param assetType - Asset type ('logo' or 'style_image')
 * @param slot - Slot number (1 or 2)
 * @returns Link UUID
 */
export async function assignBrandAsset(
  brandId: string,
  assetId: string,
  assetType: BrandAssetType,
  slot: number
): Promise<string> {
  const response = await apiClient.post<{ link_id: string }>(
    `${API_BASE}/${brandId}/assets/${assetId}/assign`,
    { asset_type: assetType, slot }
  );
  return response.data.link_id;
}

/**
 * Remove asset from a brand slot.
 *
 * @param brandId - Brand UUID
 * @param assetType - Asset type ('logo' or 'style_image')
 * @param slot - Slot number (1 or 2)
 * @returns True if removed, false if slot was already empty
 */
export async function removeBrandAsset(
  brandId: string,
  assetType: BrandAssetType,
  slot: number
): Promise<boolean> {
  const response = await apiClient.delete<{ removed: boolean }>(
    `${API_BASE}/${brandId}/assets/${assetType}/${slot}`
  );
  return response.data.removed;
}

/**
 * Update logo placement setting.
 *
 * @param brandId - Brand UUID
 * @param enabled - Whether to enable logo placement in generated images
 */
export async function updateLogoPlacement(
  brandId: string,
  enabled: boolean
): Promise<void> {
  await apiClient.patch(`${API_BASE}/${brandId}/logo-placement`, { enabled });
}

/**
 * Upload file and assign to brand asset slot.
 *
 * @param brandId - Brand UUID
 * @param file - Image file to upload
 * @param assetType - 'logo' or 'style_image'
 * @param slot - Slot number (1 or 2)
 * @returns Assigned slot data with presigned URL
 */
export async function uploadAndAssignBrandAsset(
  brandId: string,
  file: File,
  assetType: BrandAssetType,
  slot: number
): Promise<BrandAssetSlot> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('asset_type', assetType);
  formData.append('slot', slot.toString());

  const response = await apiClient.post<{ slot: BrandAssetSlot }>(
    `${API_BASE}/${brandId}/assets/upload`,
    formData
  );
  return response.data.slot;
}
